<template>
  <div
      class="editable-element-drag-drop"
      :class="{ 'lock': elementInfo.lock }"
      :style="{
      width: '100%',
      position: 'relative',
      zIndex: 101
    }"
  >
    <div
        :class="cssClasses"
        ref="elementRef"
        :style="{
          width: '100%',
          height: elementInfo.vertical ? elementInfo.height + 'px' : '100%',
          backgroundColor: elementInfo.fill,
          opacity: elementInfo.opacity,
          textShadow: shadowStyle,
          lineHeight: elementInfo.lineHeight,
          letterSpacing: (elementInfo.wordSpace || 0) + 'px',
          color: elementInfo.defaultColor,
          // fontFamily: elementInfo.defaultFontName,
        }"
    >
      <div v-for="row in chosenLayout" :key="row.id" class="row draggable-row">
        <div
            v-for="child in row.children"
            :key="child.id"
            :class="{
                    'col': true,
                  }"
            :style="getContainerParentStyle(child)"
        >
          <div :style="getParentStyle(child)" :class="{ [child.widgetClass]: child.widgetClass }">
            <Draggable
                :id="`${elementInfo.id}-${child.id}`"
                :list="getDropZone(`${elementInfo.id}-${child.id}`)"
                :class="{
                    'widget-group': true,
                  }"

                group="elements"
                itemKey="name"
                :disabled="disableDrag"
                :style="getSubsectionAlignment(child)"
            >
              <template #item="element">
                <div :style="getDimensionStyle(element.element.data, child)"
                     :key="uuid.v4() + recalculate + updated"
                     class="item-container"
                     :class="{
                          [element.element.data.widgetClass]: element.element.data.widgetClass
                        }"
                >

                  <AiChatButton v-if="element.element.data.aiButton" :element-data="element.element.data" />

                  <div class="widget-holder"
                       :id="`${element.element.data.id}`"
                       :style="getStyle(element.element.data, child)"
                  >
                    <component
                        class="element-component"
                        :viewer="true"
                        :elementData="element.element.data"
                        :is="element.element.component"
                        :style="{ width: getStyle(element.element.data, child).justifyContent === 'space-between' ? '100%' : '', maxWidth: '100vw'}"
                    />
                  </div>
                </div>
              </template>
            </Draggable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, markRaw, onBeforeMount, onMounted, PropType, ref, watch} from 'vue'
import { PPTDragDropElement} from '@/types/slides'
import {useGivingTreeStore, useMainStore, useSlidesStore} from '@/store'
import 'bootstrap/dist/css/bootstrap.css';
import Draggable from 'vuedraggable'

import CsvSelectorWidget from '@/views/components/element/DragDropElement/components/CsvSelectorWidget.vue'
import {useMicroservice} from '@/hooks/index'
import RegularTextWidget from '@/views/components/element/DragDropElement/components/RegularTextWidget.vue'
import ImageWidget from "@/views/components/element/DragDropElement/components/ImageWidget.vue";
import ChartWidget from "@/views/components/element/ChartElement/index.vue"
import TableWidget from "@/views/components/element/TableElement/index.vue"
import ModelData from "@/views/components/element/DragDropElement/components/ModelDataWidget.vue"

import {storeToRefs} from 'pinia'
import {useWidgetStore} from '@/store/widgets'
import useElementShadow from '@/views/components/element/hooks/useElementShadow'
import useHistorySnapshot from "@/hooks/useHistorySnapshot";
import ButtonWidget from "@/views/components/element/DragDropElement/components/ButtonWidget.vue";
import { applyRules } from "@/utils/common";
import {uuid} from "vue-uuid";
import AiChatButton from "@/views/components/element/DragDropElement/components/AiChatButton.vue";

const { addHistorySnapshot } = useHistorySnapshot()
const api = useMicroservice()

const props = defineProps({
  elementInfo: {
    type: Object as PropType<PPTDragDropElement>,
    required: true,
  },
  sectionId: {
    type: String,
    required: true,
  },
  sectionRowId: {
    type: String,
    required: true,
  },
  sectionStyle: {
    type: Object
  },
  sectionUsePrototypeTile: {
    type: Boolean
  },
  sectionPrototypeTileStyle: {
    type: Object
  }
})

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  }
  else {
    return defaultValue
  }
}

const getAttrNumber = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return 0
  }
  return element?.style[all] ? element?.style[all] : element?.style[which]
}

const getAttr = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return 0
  }
  return element?.style[all] ? px(element?.style[all], 0) : px(element?.style[which], 0)
}

//Need null to return to know if we use baseStyles or overrideStyles in getStyle and getDimensionStyle
const getAttrStyle = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return null
  }
  return element?.style[all] ? px(element?.style[all], 0) : px(element?.style[which], 0)
};

const highlightItem = (data) => {

  return selectedWidgets.value.includes(data)
}

const getPaddingWidth = (element) => {
  return getAttrNumber(element, 'padding', 'paddingLeft') + getAttrNumber(element, 'padding', 'paddingRight') +
      (highlightItem(element) ? 5 : 0)
}

const getPaddingHeight = (element) => {
  return getAttrNumber(element, 'padding', 'paddingTop') + getAttrNumber(element, 'padding', 'paddingBottom') +
      (highlightItem(element) ? 5 : 0)
}

const getMarginHeight = (element) => {
  return getAttrNumber(element, 'margin', 'marginTop') + getAttrNumber(element, 'margin', 'marginBottom')
}

const getMarginWidth = (element) => {
  return getAttrNumber(element, 'margin', 'marginLeft') + getAttrNumber(element, 'margin', 'marginRight')
}

// For both width and height:
// The override hierarchy (from the highest priority to lowest):
// Individual widget styles, section all tiles settings, subsection all tiles settings, prototype tile styles
const computeWidgetDimensions = (styleSource, element, subsection, fallback: any = {}) => {
  const getWidth = () => {
    if (props.sectionStyle?.allTilesWidth) {
      return px(props.sectionStyle.allTilesWidth, null)
    }
    if (subsection?.style?.allTilesWidth) {
      return px(subsection.style.allTilesWidth, null)
    }
    const baseWidth = styleSource?.containerWidth || styleSource?.imageWidth
    if (baseWidth) {
      return `${baseWidth + getPaddingWidth(element)}px`
    }
    return typeof fallback.width === 'number'
        ? px(fallback.width, null)
        : fallback.width ?? `calc(100% - ${px(getMarginWidth(element), '0px')})`
  }

  const getHeight = () => {
    if (props.sectionStyle?.allTilesHeight) {
      return px(props.sectionStyle.allTilesHeight, null)
    }
    if (subsection?.style?.allTilesHeight) {
      return px(subsection.style.allTilesHeight, null)
    }
    const baseHeight = styleSource?.containerHeight || styleSource?.imageHeight
    if (baseHeight) {
      return `${baseHeight + getPaddingHeight(element)}px`
    }
    return typeof fallback.height === 'number'
        ? px(fallback.height, null)
        : fallback.height ?? `auto`
  }

  return {
    widgetWidth: getWidth(),
    widgetHeight: getHeight(),
  }
}

const getDimensionStyle = (element, subsection) => {
  // Styles from prototype tile
  let baseStyles: any = {}
  // Styles from individual widget, overrides the prototype tile styles for just this one widget
  let overrideStyles: any

  if (subsection.usePrototypeTile || props.sectionUsePrototypeTile) {
    const prototypeElement = {
      style: props.sectionPrototypeTileStyle ? props.sectionPrototypeTileStyle : subsection.prototypeTileStyle
    }

    const { widgetWidth, widgetHeight } = computeWidgetDimensions(prototypeElement.style, prototypeElement, subsection)

    baseStyles = {
      borderTop: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderTop')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderRight: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderRight')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderBottom: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderBottom')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderLeft: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderLeft')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderRadius: px(prototypeElement?.style?.borderRadius, '0'),
      backgroundColor: prototypeElement?.style?.backgroundColor,
      paddingTop: getAttrStyle(prototypeElement, 'padding', 'paddingTop'),
      paddingRight: getAttrStyle(prototypeElement, 'padding', 'paddingRight'),
      paddingBottom: getAttrStyle(prototypeElement, 'padding', 'paddingBottom'),
      paddingLeft: getAttrStyle(prototypeElement, 'padding', 'paddingLeft'),
      marginTop: getAttrStyle(prototypeElement, 'margin', 'marginTop'),
      marginRight: getAttrStyle(prototypeElement, 'margin', 'marginRight'),
      marginBottom: getAttrStyle(prototypeElement, 'margin', 'marginBottom'),
      marginLeft: getAttrStyle(prototypeElement, 'margin', 'marginLeft'),
      height: widgetHeight,
      width: widgetWidth,
      maxWidth: '100vw'
    }
  }

  const { widgetWidth, widgetHeight } = computeWidgetDimensions(element?.style, element, subsection, baseStyles)

  overrideStyles = {
    borderTop: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderTop') ?? baseStyles.borderTop?.split(' ')[0]?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderTop,
    borderRight: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderRight') ?? baseStyles.borderRight?.split(' ')[0]?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderRight,
    borderBottom: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderBottom') ?? baseStyles.borderBottom?.split(' ')[0]?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderBottom,
    borderLeft: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderLeft') ?? baseStyles.borderLeft?.split(' ')[0]?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderLeft,
    borderRadius: px(element?.style?.borderRadius, '0') || baseStyles.borderRadius,
    backgroundColor: element?.style?.backgroundColor || baseStyles.backgroundColor,
    paddingTop: getAttrStyle(element, 'padding', 'paddingTop') || baseStyles.paddingTop,
    paddingRight: getAttrStyle(element, 'padding', 'paddingRight') || baseStyles.paddingRight,
    paddingBottom: getAttrStyle(element, 'padding', 'paddingBottom') || baseStyles.paddingBottom,
    paddingLeft: getAttrStyle(element, 'padding', 'paddingLeft') || baseStyles.paddingLeft,
    marginTop: getAttrStyle(element, 'margin', 'marginTop') || baseStyles.marginTop,
    marginRight: getAttrStyle(element, 'margin', 'marginRight') || baseStyles.marginRight,
    marginBottom: getAttrStyle(element, 'margin', 'marginBottom') || baseStyles.marginBottom,
    marginLeft: getAttrStyle(element, 'margin', 'marginLeft') || baseStyles.marginLeft,
    height: widgetHeight,
    width: widgetWidth,
    maxWidth: '100vw'
  }

  const finalStyles = { ...baseStyles, ...overrideStyles }

  if (element.ruleIds && element.type !== 'table') {
    const results = applyRules(element, rules.value)
    return { ...finalStyles, ...results }
  }

  element.alternateColor = null

  return finalStyles
}

const getSubsectionAlignment = (element) => {
  // we have to have a column flex so things get flipped
  let justifyContent = element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align
  if (justifyContent === 'left') {
    justifyContent = 'start'
  }
  else if (justifyContent === 'right') {
    justifyContent = 'end'
  }

  return {
    justifyContent: element?.style?.valign,
    alignItems: justifyContent
  }
}

const getContainerParentStyle = (element) => {
  const style = {
    margin: 0,
    padding: 0,
    borderRadius: px(element?.style?.borderRadius, '0'),
    lineHeight: element?.style?.lineHeight ? element?.style?.lineHeight : 'normal',
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    height: px((element?.style?.containerHeight || element?.style?.imageHeight)
        + getAttrNumber(element, 'margin', 'marginTop') + getAttrNumber(element, 'margin', 'marginBottom'),'auto'),
    width: px(element?.style?.containerWidth || element?.style?.imageWidth,'auto'),
    backgroundColor: element?.style?.backgroundColor,
  }

  if (element?.style?.containerWidth) {
    style.flex = `0 0 ${px(element?.style?.containerWidth || element?.style?.imageWidth,'auto')}`
  }
  else {
    style.flexGrow = element['flex-grow'] || '1'
    style.flexShrink = 0
    style.flexBasis = 0
    style.display = element['hidden'] ? 'none' : 'flex'
  }

  if (element.ruleIds && element.type !== 'table') {
    const results = applyRules(element, rules.value);
    return { ...style, ...results };
  } else {
    element.alternateColor = null;
  }

  return style
}




const getParentStyle = (element) => {
  const borderColor = element?.style?.borderColor


  return {
    marginTop: getAttr(element, 'margin', 'marginTop'),
    marginRight: getAttr(element, 'margin', 'marginRight'),
    marginBottom: getAttr(element, 'margin', 'marginBottom'),
    marginLeft: getAttr(element, 'margin', 'marginLeft'),
    paddingTop: getAttr(element, 'padding', 'paddingTop'),
    paddingRight: getAttr(element, 'padding', 'paddingRight'),
    paddingBottom: getAttr(element, 'padding', 'paddingBottom'),
    paddingLeft: getAttr(element, 'padding', 'paddingLeft'),
    display: 'flex',
    lineHeight: element?.style?.lineHeight ? element?.style?.lineHeight : 'normal',
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    borderTop: `${getAttr(element, 'borderWidth', 'borderTop')} solid ${borderColor}`,
    borderRight: `${getAttr(element, 'borderWidth', 'borderRight')} solid ${borderColor}`,
    borderBottom: `${getAttr(element, 'borderWidth', 'borderBottom')} solid ${borderColor}`,
    borderLeft: `${getAttr(element, 'borderWidth', 'borderLeft')} solid ${borderColor}`,
    borderRadius: px(element?.style?.borderRadius, '0'),
    backgroundColor: element?.style?.backgroundColor,
    height: px(element?.style?.containerHeight,'auto'),
    width: px(element?.style?.containerWidth,'auto')
  }
}

const getStyle = (element: any, subsection: any) => {
  let baseStyles: any = {}
  let overrideStyles: any

  if (subsection.usePrototypeTile || props.sectionUsePrototypeTile) {
    const prototypeElement = {
      style: props.sectionPrototypeTileStyle ? props.sectionPrototypeTileStyle : subsection.prototypeTileStyle
    }

    // For any prosemirror styles, no need to add to override styles
    // because prosemirror will override them automatically by adding to text span
    baseStyles = {
      fontSize: prototypeElement?.style?.fontSize, //prosemirror
      color: prototypeElement?.style?.color, //prosemirror
      fontWeight: prototypeElement?.style?.fontWeight, //prosemirror
      fontStyle: prototypeElement?.style?.fontStyle, //prosemirror
      textDecoration: prototypeElement?.style?.textDecoration, //prosemirror
      textAlign: prototypeElement?.style?.align,
      display: 'flex',
      lineHeight: prototypeElement?.style?.lineHeight ? prototypeElement?.style?.lineHeight : 'normal',
      justifyContent: prototypeElement?.style?.justifyContent !== '' ? prototypeElement?.style?.justifyContent : prototypeElement?.style?.align,
      alignItems: prototypeElement?.style?.valign,
      height: px(prototypeElement?.style?.containerHeight || prototypeElement?.style?.imageHeight, '100%'),
      width: px(prototypeElement?.style?.containerWidth || prototypeElement?.style?.imageWidth, '100%'),
      maxWidth: '100vw'
    }
  }

  overrideStyles = {
    textAlign: element?.style?.align || baseStyles.textAlign,
    display: 'flex',
    lineHeight: element?.style?.lineHeight || baseStyles.lineHeight || 'normal',
    justifyContent: element?.style?.justifyContent || baseStyles.justifyContent || element?.style?.align,
    alignItems: element?.style?.valign || baseStyles.alignItems,
    height: px(element?.style?.containerHeight || element?.style?.imageHeight, '100%') || baseStyles.height,
    width: px(element?.style?.containerWidth || element?.style?.imageWidth, '100%') || baseStyles.width,
    maxWidth: '100vw'
  }

  const finalStyles = { ...baseStyles, ...overrideStyles }

  if (element.ruleIds && element.type !== 'table') {
    const results = applyRules(element, rules.value);
    return { ...finalStyles, ...results }
  }

  element.alternateColor = null

  return finalStyles
};

const cssClasses = ref('element-content dragdrop')


const reactiveJson = ref(null)
const elementRef = ref<HTMLElement | null>(null)

const mainStore = useMainStore()
const {recalculate} = storeToRefs(mainStore)
const { updated } = storeToRefs(useGivingTreeStore())
const widgetStore = useWidgetStore()
const { selectedWidgets, selectedWidget, selectedWidgetGroupId, selectedSubsectionRowId } = storeToRefs(widgetStore)
const slidesStore = useSlidesStore()
const { rules } = storeToRefs(slidesStore)
const chosenLayout = ref([])


const disableDrag = ref(true)

const shadow = computed(() => props.elementInfo.shadow)
const {shadowStyle} = useElementShadow(shadow)
const widgetClass = computed(() => selectedWidget.value.widgetClass || '')

const componentTypes = {
  'reg_text': markRaw(RegularTextWidget),
  'model_data': markRaw(ModelData),
  'csv_selector': markRaw(CsvSelectorWidget),
  'image': markRaw(ImageWidget),
  'chart': markRaw(ChartWidget),
  'table': markRaw(TableWidget),
  'button': markRaw(ButtonWidget)
}

onBeforeMount(async () => {
  const findDragDrop: PPTDragDropElement = <PPTDragDropElement>slidesStore.getElementsByTypeAllSlides('dragdrop').find((zone) => zone.id === props.elementInfo?.id)
  reactiveJson.value = findDragDrop ? findDragDrop.widgets : []

})

onMounted(() => {
  chosenLayout.value = props.elementInfo.layout

})

const getDropZone = ((parentId) => {
  try {
    // can't do equals here because we used to also have sublayouts that no longer exist
    const parentExists = reactiveJson.value.filter(item => item.parent.indexOf(parentId) === 0)
    if (parentExists.length === 0) {
      reactiveJson.value = [...reactiveJson.value, {
        'parent': parentId,
        'children': []
      }]
    }

    return replaceTextWithComponents(parentExists ? parentExists.flatMap(obj => obj.children) : []);
  } catch(e) {
    console.log('catch', e)
  }
})

const replaceTextWithComponents = ((children) => {
  const formattedChildren = []
  children.forEach((item) => {
    const component = componentTypes[item.type]
    formattedChildren.push({
      data: item,
      component
    })
  })

  return formattedChildren
})
</script>

<style lang="scss" scoped>
.draggable-row {
  --bs-gutter-x: 0;
  --bs-gutter-y: 0;
}

.widget-group {

  min-height: 30px;

  color-scheme: light;
  user-select: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;
}

.widget-holder {
  position: relative;
  display: inline-block;
  justify-content: center;
  align-items: center;
  background-color: transparent;
}

.rotate-wrapper {
  width: 100%;
  height: 100%;
}


.item-container {
  border-radius: 10px;
  border: solid transparent 1px;
  justify-content: center;
  display: flex;
  flex-direction: row;
  position: relative;
}

.subsection {
  border: 1px solid rgba(255, 165, 0,.5) !important;
}
</style>
